import { useWarrantyData } from "app/hooks";
import { useHandleZakelijkBankTransfer } from "app/hooks/payment/useHandleZakelijkBankTransfer";
import { useAnalytics } from "app/hooks/shared/analytics/useAnalytics";
import { useVoucherOrderAddition } from "app/hooks/shared/useVoucherOrderAddition";
import { useCreateOrderWarranty } from "app/hooks/warranty/useCreateOrderWarranty";
import { useOrderContext } from "app/providers/OrderProvider";
import { usePaymentContext } from "pages/payment/providers/PaymentProvider";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { voucherState } from "shared/store/voucher/voucherStore";
import Button, { ButtonTypes } from "shared/ui/button";
import { PaymentActions } from "shared/utils/payment-methods";
import { useSnapshot } from "valtio";
import VoucherModalOrder from "widgets/shared/add-voucher-modal/VoucherModalOrder";

const decideLaterWarranties = ["decide_later", "no_package_now"];
const PaymentFooter = ({ hideVoucher = false }: { hideVoucher?: boolean }) => {
  const {
    onPaymentSubmit,
    canProceed,
    isCreatePaymentLinkLoading,
    isCreateStandardOrderPaymentLinkLoading,
    getPaymentTypeList,
    paymentAction,
    paymentMethod,
    bank,
  } = usePaymentContext();

  const [searchParams] = useSearchParams();

  const { onAddPaymentInfo } = useAnalytics();

  // Get order ID and order type from URL
  const orderID = searchParams.get("orderId") || searchParams.get("pOrderId");
  const orderType = searchParams.get("orderId") ? "d" : "p";

  const { addWarrantyToOrder, isLoading } = useCreateOrderWarranty();

  const { order, warrantyPackage } = useOrderContext();
  const { warranties, warrantyPackagesDataRequest } = useWarrantyData();

  //Handle bank transfer for zakelijk orders
  const { handleZakelijkBankTransfer, isBankTransferLoading } =
    useHandleZakelijkBankTransfer();

  // Voucher helpers
  const { addVoucherToOrder, status } = useVoucherOrderAddition({
    orderID,
    orderType: orderType,
    toastEnabled: false,
  });

  const { voucher } = useSnapshot(voucherState);

  // Decide if the page is in loading state
  const isLoadingState =
    isCreatePaymentLinkLoading ||
    isCreateStandardOrderPaymentLinkLoading ||
    isLoading ||
    status === "loading" ||
    isBankTransferLoading;

  const getWarrantyPackageID = () => {
    if (warrantyPackage?.packageId === "no_package_full") {
      return (
        warrantyPackagesDataRequest?.data?.freeWarrantyPackage?.packageId ?? ""
      );
    }
    return warrantyPackage?.packageId ?? "";
  };

  const alreadyPaid = order?.paymentData && order?.paymentData.paid > 0;

  /**
   *
   * * Submit payment for std order or door order
   * ! Doors:
   * * 1) Update order with warranty package ( if needed )
   * * 2) Payment
   *
   * ! Standard Orders:
   * *  1) Payment
   */

  const handlePaymentSubmit = async () => {
    if (paymentAction === PaymentActions.BANK_TRANSFER) {
      handleZakelijkBankTransfer({
        orderId: order!.id,
        payload: { paymentStatus: "onAccount" },
      });
    } else {
      // Conditions to return early
      const shouldAssignWarranty =
        orderType === "d" && // If the order is a door order
        order?.warrantyPackage?.packageId === undefined && // If the order does not have a warranty package yet
        orderID && // If the order ID is available
        warrantyPackage?.packageId; // If the warranty package is available
      // warrantyPackage.price > 0; // If the warranty package has a price

      // Get the warranty package ID
      const warrantyPackageID = getWarrantyPackageID();

      if (
        shouldAssignWarranty &&
        !decideLaterWarranties.includes(warrantyPackageID)
      ) {
        try {
          // Assign warranty to order
          await addWarrantyToOrder({
            orderId: orderID,
            packageId: warrantyPackageID,
          });
        } catch (error) {
          toast.error("Er is een fout opgetreden. Probeer het opnieuw.");
        }
      }

      if (voucher) {
        try {
          // Assign voucher to order
          await addVoucherToOrder(voucher.code);
        } catch (error) {
          toast.error("Er is een fout opgetreden. Probeer het opnieuw.");
        }
      }

      // Submit payment in the end as usual
      onPaymentSubmit();
    }

    // Submit analytics event
    if (orderType === "d") {
      const action = getPaymentTypeList([]).find(
        (type) => type.action === paymentAction,
      );
      const method = action?.methods.find(
        (method) => method.id === paymentMethod,
      );
      const paymentBank = method?.banks?.find((b) => b.id === bank);

      onAddPaymentInfo(order!, {
        action: action?.action || "-",
        method: method?.visibleName || "-",
        bank: paymentBank?.visibleName || "-",
      });
    }
  };

  return (
    <>
      <div
        className={`gg-grid gg-items-center gg-gap-y-5 gg-bg-dark-blue gg-px-8 gg-py-14 gg-text-white sm:gg-flex sm:gg-justify-between sm:gg-px-14 2xl:gg-px-32`}
      >
        {!hideVoucher && (
          <VoucherModalOrder
            orderID={orderID}
            orderType={orderType}
            alreadyPaid={alreadyPaid}
          />
        )}

        {hideVoucher && <div className=""></div>}

        <Button
          loading={isLoadingState}
          onClick={handlePaymentSubmit}
          disabled={!canProceed() || isLoadingState}
          type={ButtonTypes.YELLOW}
          className="gg-flex gg-px-10 gg-py-3"
        >
          Betalen
        </Button>
      </div>
    </>
  );
};
export default PaymentFooter;
